/** Display formatting for the account panels. English output is unchanged; other locales get their separators (#799). */
import { cents, localizeNumber as localize, percent, usd } from '../i18n/format';

// English-formatted magnitudes; usd()/localize swap in the locale's separators and $ placement.
const usd2 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function bad(n: number | null | undefined): n is null | undefined {
  return n === null || n === undefined || !Number.isFinite(n);
}

export function fmtUsd(n: number | null | undefined): string {
  if (bad(n)) return '—';
  const s = usd2.format(Math.abs(n));
  return n < 0 && s !== '0.00' ? usd(s, '-') : usd(s);
}

export function fmtSignedUsd(n: number | null | undefined): string {
  if (bad(n)) return '—';
  const s = usd2.format(Math.abs(n));
  if (s === '0.00') return usd('0.00');
  return usd(s, n < 0 ? '-' : '+');
}

/** Plain number with up to `maxDp` decimals (trailing zeros dropped). */
export function fmtNum(n: number | null | undefined, maxDp = 6): string {
  if (bad(n)) return '—';
  return localize(new Intl.NumberFormat('en-US', { maximumFractionDigits: maxDp }).format(n));
}

/** Outcome share price (0..1) as cents, e.g. 0.6904 -> "69.0¢". */
export function fmtCents(px: number | null | undefined): string {
  if (bad(px)) return '—';
  return cents((px * 100).toFixed(1));
}

/** Perp price: 5 significant figures, at most 6 decimals (HL display rule). */
export function fmtPx(px: number | null | undefined): string {
  if (bad(px) || px === 0) return '—';
  return localize(new Intl.NumberFormat('en-US', { maximumFractionDigits: 6 }).format(Number(px.toPrecision(5))));
}

export function fmtPct(r: number | null | undefined): string {
  if (bad(r)) return '—';
  const v = r * 100;
  return `${v > 0 ? '+' : ''}${percent(v.toFixed(2))}`;
}

export function pnlClass(n: number | null | undefined): string {
  if (bad(n) || Math.abs(n) < 0.005) return 'text-muted';
  return n > 0 ? 'text-yes' : 'text-no';
}
